import React, { useState, useMemo } from 'react';
import ToolLayout from '../components/ToolLayout';

interface ConversionOption {
  key: string;
  name: string;
  description: string;
  example: string;
  icon: string;
}

const CaseConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [selectedConversions, setSelectedConversions] = useState<string[]>(['camelCase', 'snake_case', 'kebab-case']);

  const conversionOptions: ConversionOption[] = [
    {
      key: 'camelCase',
      name: 'camelCase',
      description: 'Viết hoa chữ cái đầu của mỗi từ (trừ từ đầu tiên)',
      example: 'myVariableName',
      icon: '🐪'
    },
    {
      key: 'PascalCase',
      name: 'PascalCase',
      description: 'Viết hoa chữ cái đầu của mỗi từ (bao gồm từ đầu tiên)',
      example: 'MyVariableName',
      icon: '📐'
    },
    {
      key: 'snake_case',
      name: 'snake_case',
      description: 'Các từ được nối bằng dấu gạch dưới',
      example: 'my_variable_name',
      icon: '🐍'
    },
    {
      key: 'SCREAMING_SNAKE_CASE',
      name: 'SCREAMING_SNAKE_CASE',
      description: 'Snake case với tất cả chữ cái viết hoa',
      example: 'MY_VARIABLE_NAME',
      icon: '📢'
    },
    {
      key: 'kebab-case',
      name: 'kebab-case',
      description: 'Các từ được nối bằng dấu gạch ngang',
      example: 'my-variable-name',
      icon: '🍢'
    },
    {
      key: 'UPPER-KEBAB-CASE',
      name: 'UPPER-KEBAB-CASE',
      description: 'Kebab case với tất cả chữ cái viết hoa',
      example: 'MY-VARIABLE-NAME',
      icon: '🍢'
    },
    {
      key: 'dot.case',
      name: 'dot.case',
      description: 'Các từ được nối bằng dấu chấm',
      example: 'my.variable.name',
      icon: '🔸'
    },
    {
      key: 'path/case',
      name: 'path/case',
      description: 'Các từ được nối bằng dấu gạch chéo',
      example: 'my/variable/name',
      icon: '📁'
    },
    {
      key: 'Title Case',
      name: 'Title Case',
      description: 'Viết hoa chữ cái đầu của mỗi từ, có khoảng trắng',
      example: 'My Variable Name',
      icon: '📝'
    },
    {
      key: 'Sentence case',
      name: 'Sentence case',
      description: 'Viết hoa chữ cái đầu tiên, các từ cách nhau bằng khoảng trắng',
      example: 'My variable name',
      icon: '📖'
    },
    {
      key: 'lowercase',
      name: 'lowercase',
      description: 'Tất cả chữ cái viết thường, có khoảng trắng',
      example: 'my variable name',
      icon: '🔤'
    },
    {
      key: 'UPPERCASE',
      name: 'UPPERCASE',
      description: 'Tất cả chữ cái viết hoa, có khoảng trắng',
      example: 'MY VARIABLE NAME',
      icon: '🔠'
    }
  ];

  // Utility functions for text conversion
  const splitIntoWords = (text: string): string[] => {
    // Split by various delimiters and camelCase boundaries
    return text
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Split PascalCase
      .split(/[\s\-_.\/]+/) // Split by common delimiters
      .filter(word => word.length > 0)
      .map(word => word.toLowerCase());
  };

  const convertText = (text: string, conversionType: string): string => {
    if (!text.trim()) {
      return '';
    }

    const words = splitIntoWords(text);

    switch (conversionType) {
      case 'camelCase':
        return words[0] + words.slice(1).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
      
      case 'PascalCase':
        return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
      
      case 'snake_case':
        return words.join('_');
      
      case 'SCREAMING_SNAKE_CASE':
        return words.map(word => word.toUpperCase()).join('_');
      
      case 'kebab-case':
        return words.join('-');
      
      case 'UPPER-KEBAB-CASE':
        return words.map(word => word.toUpperCase()).join('-');
      
      case 'dot.case':
        return words.join('.');
      
      case 'path/case':
        return words.join('/');
      
      case 'Title Case':
        return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      
      case 'Sentence case':
        return words[0].charAt(0).toUpperCase() + words[0].slice(1) + (words.length > 1 ? ' ' + words.slice(1).join(' ') : '');
      
      case 'lowercase':
        return words.join(' ');
      
      case 'UPPERCASE':
        return words.map(word => word.toUpperCase()).join(' ');
      
      default:
        return text;
    }
  };

  const conversions = useMemo(() => {
    return conversionOptions
      .filter(option => selectedConversions.includes(option.key))
      .map(option => ({
        ...option,
        result: convertText(input, option.key)
      }));
  }, [input, selectedConversions]);

  const toggleConversion = (key: string) => {
    if (selectedConversions.includes(key)) {
      setSelectedConversions(prev => prev.filter(k => k !== key));
    } else {
      setSelectedConversions(prev => [...prev, key]);
    }
  };

  const selectAllConversions = () => {
    setSelectedConversions(conversionOptions.map(option => option.key));
  };

  const clearAllConversions = () => {
    setSelectedConversions([]);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      // Silent fail for clipboard API
    }
  };

  const loadSampleData = () => {
    setInput('my awesome variable name');
  };

  const clearInput = () => {
    setInput('');
  };

  const parseAndSetFromCode = () => {
    // Extract variable names, function names, etc. from code
    const codePattern = /(?:var|let|const|function|class)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g;
    const matches = Array.from(input.matchAll(codePattern));
    if (matches.length > 0) {
      setInput(matches[0][1]);
    }
  };

  return (
    <ToolLayout
      title="Case Converter"
      description="Chuyển đổi kiểu viết text giữa các naming conventions phổ biến"
    >
      <div className="case-converter">
        {/* Input Section */}
        <div className="input-section">
          <div className="input-header">
            <label className="input-label">Nhập text cần chuyển đổi:</label>
            <div className="input-actions">
              <button className="btn btn-sm btn-secondary" onClick={clearInput}>
                Xóa
              </button>
              <button className="btn btn-sm btn-secondary" onClick={loadSampleData}>
                Dữ liệu mẫu
              </button>
              <button className="btn btn-sm btn-secondary" onClick={parseAndSetFromCode} title="Trích xuất biến từ code">
                Phân tích code
              </button>
            </div>
          </div>
          <textarea
            className="input-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ví dụ: my awesome variable name, myVariableName, my-variable-name..."
            rows={4}
          />
          {input && (
            <div className="input-info">
              <span className="info-text">
                Từ phát hiện: {splitIntoWords(input).length} từ - "{splitIntoWords(input).join(', ')}"
              </span>
            </div>
          )}
        </div>

        {/* Conversion Options */}
        <div className="options-section">
          <div className="options-header">
            <h3>Chọn kiểu chuyển đổi:</h3>
            <div className="options-actions">
              <button className="btn btn-sm btn-secondary" onClick={clearAllConversions}>
                Bỏ chọn tất cả
              </button>
              <button className="btn btn-sm btn-primary" onClick={selectAllConversions}>
                Chọn tất cả
              </button>
            </div>
          </div>
          
          <div className="options-grid">
            {conversionOptions.map((option) => (
              <label key={option.key} className={`option-card ${selectedConversions.includes(option.key) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedConversions.includes(option.key)}
                  onChange={() => toggleConversion(option.key)}
                  className="option-checkbox"
                />
                <div className="option-content">
                  <div className="option-header">
                    <span className="option-icon">{option.icon}</span>
                    <span className="option-name">{option.name}</span>
                  </div>
                  <p className="option-description">{option.description}</p>
                  <code className="option-example">{option.example}</code>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Results */}
        {input && conversions.length > 0 && (
          <div className="results-section">
            <h3>Kết quả chuyển đổi:</h3>
            <div className="results-grid">
              {conversions.map((conversion) => (
                <div key={conversion.key} className="result-card">
                  <div className="result-header">
                    <div className="result-title">
                      <span className="result-icon">{conversion.icon}</span>
                      <span className="result-name">{conversion.name}</span>
                    </div>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => copyToClipboard(conversion.result)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                  <div className="result-content">
                    <code className="result-text">{conversion.result}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="tips-section">
          <h4>💡 Mẹo sử dụng:</h4>
          <ul className="tips-list">
            <li>Tool tự động nhận diện các từ từ camelCase, PascalCase, snake_case, kebab-case</li>
            <li>Bạn có thể nhập cả câu hoặc đoạn text có khoảng trắng</li>
            <li>Sử dụng "Phân tích code" để trích xuất tên biến từ code</li>
            <li>Click vào icon 📋 để copy kết quả vào clipboard</li>
            <li>Hữu ích cho việc chuyển đổi giữa naming conventions trong lập trình</li>
          </ul>
        </div>
      </div>

      <style>{`
        .case-converter {
          max-width: 1200px;
          margin: 0 auto;
        }

        .input-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .input-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .input-label {
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .input-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .input-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.5;
          resize: vertical;
          min-height: 100px;
        }

        .input-textarea:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .input-info {
          margin-top: 0.75rem;
          padding: 0.5rem 0.75rem;
          background: var(--input-background);
          border-radius: 4px;
          border: 1px solid var(--border-color);
        }

        .info-text {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        .options-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .options-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .options-header h3 {
          margin: 0;
          color: var(--text-primary);
        }

        .options-actions {
          display: flex;
          gap: 0.5rem;
        }

        .options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1rem;
        }

        .option-card {
          display: block;
          padding: 1rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          background: var(--input-background);
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .option-card:hover {
          border-color: var(--primary-color);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .option-card.selected {
          border-color: var(--primary-color);
          background: rgba(66, 153, 225, 0.05);
        }

        .option-checkbox {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          margin: 0;
        }

        .option-content {
          padding-right: 2rem;
        }

        .option-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .option-icon {
          font-size: 1.2rem;
        }

        .option-name {
          font-weight: 600;
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        }

        .option-description {
          margin: 0 0 0.5rem 0;
          color: var(--text-secondary);
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .option-example {
          display: block;
          padding: 0.25rem 0.5rem;
          background: var(--card-background);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.8rem;
          color: var(--text-primary);
        }

        .results-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .results-section h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }

        .result-card {
          background: var(--input-background);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          overflow: hidden;
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: var(--card-background);
          border-bottom: 1px solid var(--border-color);
        }

        .result-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .result-icon {
          font-size: 1rem;
        }

        .result-name {
          font-weight: 600;
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
        }

        .result-content {
          padding: 0.75rem;
        }

        .result-text {
          display: block;
          width: 100%;
          padding: 0.5rem;
          background: var(--card-background);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          color: var(--text-primary);
          word-break: break-all;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.8rem;
        }

        .btn-primary {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .btn-secondary {
          background: var(--input-background);
          color: var(--text-primary);
        }

        .btn-ghost {
          background: transparent;
          border: none;
          padding: 0.25rem;
          color: var(--text-secondary);
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .btn-ghost:hover {
          background: var(--input-background);
          color: var(--text-primary);
        }

        .tips-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .tips-section h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .tips-list {
          margin: 0;
          padding-left: 1.5rem;
          color: var(--text-secondary);
        }

        .tips-list li {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
      `}</style>
    </ToolLayout>
  );
};

export default CaseConverter;
